import { IShipmentPrice } from "@/types/apiResponse.types";
import { ICalculatorDetails } from "../types";
import { Country, TransportationMode } from "@/enums";

type Props = {
  values: ICalculatorDetails;
  shipmentPricesData: IShipmentPrice;
};

// calculate all costs
export const calculateShipmentPriceGE = ({
  values: { details, fromCountry, selectedDeliveryTypeId, additionalServices },
  shipmentPricesData,
}: Props) => {
  let transportationPrice = 0;
  let additionalServicesPrice = 0;
  let oversidedParcelExtra = 0;
  let minWeight = 0;
  let parcelDuty = 0;
  let totalCost = 0;
  let guarantee = 0;

  const isValid =
    details.weight &&
    details.cost &&
    details.length &&
    details.width &&
    details.height;

  const shipmentServicestoArray = shipmentPricesData?.shipmentServices
    ? Object?.values(shipmentPricesData?.shipmentServices)
    : [];

  if (isValid && shipmentPricesData) {
    // Transportation price calculation based on counrty id
    transportationPrice =
      shipmentPricesData.deliveryPrice.fromCountryId[fromCountry][
        selectedDeliveryTypeId === TransportationMode.AIR ||
        selectedDeliveryTypeId === TransportationMode.GROUND
          ? "airPriceUsd"
          : "seaPriceUsd"
      ];

    // Minimum weight logic
    minWeight =
      Number(details.weight) < shipmentPricesData.minWeightForCalculateKg
        ? shipmentPricesData.minWeightForCalculateKg
        : Number(details.weight);

    const isChinaLand = fromCountry === Country.CHINA_LAND;
    const isUSA = fromCountry === Country.USA;
    const isChinaExpress = fromCountry === Country.CHINA_AIR;
    const isTurkey = fromCountry === Country.TURKEY;
    const isGreece = fromCountry === Country.GREECE;
    const isEngland = fromCountry === Country.ENGLAND;

    // Volumetric weight calculation
    const maxSide = Math.max(details.length, details.width, details.height);
    const volumetricWeight =
      (details.length * details.width * details.height) /
      (isChinaLand ? 5000 : 6000);

    // Final weight logic per specification 6.2
    let finalWeight: number;

    if (isUSA || isChinaLand) {
      // Apply volumetric only if max side > 50
      finalWeight =
        maxSide > shipmentPricesData.minimumAmountOfParcelSidesCentimeters
          ? Math.max(Number(details.weight), volumetricWeight)
          : Number(details.weight);
    } else if (isChinaExpress || isTurkey || isGreece || isEngland) {
      // Always use max of parcel weight and volumetric weight
      finalWeight = Math.max(Number(details.weight), volumetricWeight);
    } else {
      finalWeight = Number(details.weight);
    }

    // Use finalWeight instead of oversidedParcelExtra in subsequent calculations
    const maxWeight = Math.max(finalWeight, minWeight);

    // Calculate transportationCost
    let transportationCost = 0;

    if (
      selectedDeliveryTypeId === TransportationMode.AIR ||
      selectedDeliveryTypeId === TransportationMode.GROUND
    ) {
      transportationCost =
        transportationPrice *
        Math.max(details.weight, minWeight, oversidedParcelExtra);
    } else if (selectedDeliveryTypeId === TransportationMode.SEA) {
      transportationCost = Math.max(
        transportationPrice * maxWeight,
        shipmentPricesData?.minCostForWeightForDeliverySeaInUsd,
      );
    }

    // Parcel duty calculation with transportation cost included
    parcelDuty =
      details.cost * shipmentPricesData?.currentRateNationalBank >
      shipmentPricesData.customPriceLimitNational
        ? ((details.cost + transportationCost) *
            shipmentPricesData.parcelDutyPercent) /
          100
        : 0;

    // Additional services price calculation with rules for serviceIds
    additionalServicesPrice = additionalServices
      .filter((item: number) => item !== 113)
      .reduce((acc, service) => {
        const servicePrice =
          shipmentServicestoArray.find((item) => item.serviceId === service)
            ?.servicePriceUsd || 0;

        switch (service) {
          case 209:
            // Must be disabled if Parcel cost > customPriceLimitNational.
            if (
              details.cost * shipmentPricesData?.currentRateNationalBank <
              shipmentPricesData.customPriceLimitNational
            ) {
              return acc + servicePrice;
            }
          case 208:
            // Must be disabled if Parcel cost < customPriceLimitNational.
            if (
              details.cost * shipmentPricesData?.currentRateNationalBank >
              shipmentPricesData.customPriceLimitNational
            ) {
              return acc + servicePrice;
            }

          default:
            return acc + servicePrice;
        }
      }, 0);

    // Guarantee
    const selectedGuarantee = shipmentServicestoArray.find(
      (item) => item.serviceId === 113,
    );

    if (selectedGuarantee && additionalServices.includes(113)) {
      if (
        details.cost <
        shipmentPricesData.shipmentDeclaredCostForMinInsuranceInUsd
      ) {
        guarantee = shipmentPricesData.baseCostForServiceInsuranceInUsd;
      } else {
        guarantee =
          shipmentPricesData.baseCostForServiceInsuranceInUsd +
          ((details.cost -
            shipmentPricesData.shipmentDeclaredCostForMinInsuranceInUsd) *
            selectedGuarantee.servicePriceUsd) /
            100;
      }
    }

    // Total cost sum based on Georgian rules
    totalCost =
      transportationCost + parcelDuty + additionalServicesPrice + guarantee;
  }

  return {
    parcelDuty,
    transportationPrice,
    additionalServicesPrice: additionalServicesPrice + guarantee,
    totalCost,
    oversidedParcelExtra,
    minWeight,
  };
};
